import { Outlet, useLocation } from 'react-router-dom';
import { Box } from 'src/components/ui';
import { useContext, useEffect, useState, useRef } from 'react';
import Sidebar from './left-sidebar/Sidebar';
import { AssistantChatContext, CampaignContext } from 'src/contexts';
import TopBar from './top-bar/TopBar';
import { usePopup } from 'src/contexts/PopupContext';
import useWindowSize from 'src/hooks/useWindowSize';
import UserContext from 'src/contexts/user/UserContext';
import {
	BREAKPOINTS,
	RIGHT_SIDEBAR_WIDTH,
	RIGHT_SIDEBAR_WIDTH_MOBILE,
	RIGHT_SIDEBAR_WIDTH_COLLAPSED,
	TOP_BAR_HEIGHT,
	LEFT_SIDEBAR_WIDTH,
	LEFT_SIDEBAR_WIDTH_COLLAPSED,
} from 'src/components/campaigns/utils/constants';
import SelectTemplateModal from 'src/components/campaigns/design-directions/SelectTemplateModal';
import TrialEndingModal from 'src/components/modals/trial-ending-modal/TrialEndingModal';
import AssistantChat from 'src/components/assistantChat';
import { useRealtimeChatContext } from 'src/fusion-copilot/hooks/useRealtimeChatContext';
import { useTrialModal } from 'src/hooks/useTrialModal';
import { RightSidebar } from './right-sidebar/RightSidebar';
import { useRightSidebar } from './right-sidebar/useRightSidebar';
import { RightSidebarProvider } from 'src/contexts/app/RightSidebarContext';
import { LeftSidebarProvider } from 'src/contexts/app/LeftSidebarContext';
import { useLeftSidebarContext } from 'src/contexts/app/LeftSidebarContext';
import { WelcomeWizard, MenuTour, GuidedCursor } from 'src/components/onboarding';
import { useOnboarding } from 'src/contexts/onboarding';

const MainLayoutContent = () => {
	const { isChatOpen } = useContext(AssistantChatContext);
	const { isScreenBlocked } = useRealtimeChatContext();
	const { pathname } = useLocation();
	const { width } = useWindowSize();
	const { user, subscription } = useContext(UserContext);
	const { setAdTemplateModalOpen, isAdTemplateModalOpen } = usePopup();
	const isCampaignPath = pathname.includes('/projects/campaigns/');
	const { showGuidedCursor, closeGuidedCursor, setWizard1Completed } = useOnboarding();
	const {
		isSidebarOpen,
		activePanel,
		handleOpenSidebar,
		handlePanelToggle,
		handleCloseSidebar,
	} = useRightSidebar();
	const { isOpen: isLeftSidebarOpen, closeSidebar: closeLeftSidebar, openSidebar: openLeftSidebar } = useLeftSidebarContext();
	const { isEditorMode } = useContext(CampaignContext);

	const [
		copilotClosedAfterEmailVerification,
		setCopilotClosedAfterEmailVerification,
	] = useState(false);
	const [isFromEmailVerification, setIsFromEmailVerification] = useState(
		sessionStorage.getItem('copilotState') !== null,
	);
	const sidebarWasOpenRef = useRef(false);
	const previousPathnameRef = useRef(pathname);
	const hasInitializedRef = useRef(false);
	const userOpenedSidebarRef = useRef(false); // Track if user manually opened sidebar


	// Track when sidebar opens
	useEffect(() => {
		if (isSidebarOpen && activePanel === 'copilot') {
			sidebarWasOpenRef.current = true;
		}
	}, [isSidebarOpen, activePanel]);

	// Detect when copilot closes after email verification
	useEffect(() => {
		// Only mark as closed if:
		// 1. Coming from email verification AND
		// 2. Sidebar is not open AND
		// 3. Sidebar was previously open
		if (
			isFromEmailVerification &&
			!isSidebarOpen &&
			sidebarWasOpenRef.current
		) {
			setCopilotClosedAfterEmailVerification(true);
			setIsFromEmailVerification(false); // Mark email verification flow as complete
			sidebarWasOpenRef.current = false; // Reset for next time
		}
	}, [isFromEmailVerification, isSidebarOpen]);

	const { isTrialModalOpen, onTrialModalClose } = useTrialModal(subscription);

	// Track when user manually toggles the left sidebar
	useEffect(() => {
		// If sidebar opens while on campaign path, user opened it manually
		if (isLeftSidebarOpen && isCampaignPath) {
			userOpenedSidebarRef.current = true;
			console.log('🔵 [MainLayout] User manually opened sidebar on campaign page');
		}
		// If sidebar closes while on campaign path and user had opened it, user closed it
		else if (!isLeftSidebarOpen && isCampaignPath && userOpenedSidebarRef.current) {
			userOpenedSidebarRef.current = false;
			console.log('🔵 [MainLayout] User manually closed sidebar on campaign page');
		}
	}, [isLeftSidebarOpen, isCampaignPath]);

	useEffect(() => {
		// Initial mount: always close sidebar if already on campaign page
		if (!hasInitializedRef.current) {
			hasInitializedRef.current = true;
			if (isCampaignPath) {
				handleCloseSidebar(); // Close right sidebar (copilot)
				closeLeftSidebar(); // Always close left sidebar on campaign pages
			}
			return;
		}

		// Only close/open sidebar when pathname actually changes (navigation happens)
		const previousPath = previousPathnameRef.current;
		const wasCampaignPath = previousPath.includes('/projects/campaigns/');

		// Update ref for next comparison
		previousPathnameRef.current = pathname;

		// Entering campaign page - always close sidebars
		if (isCampaignPath && !wasCampaignPath) {
			handleCloseSidebar(); // Close right sidebar (copilot)
			closeLeftSidebar(); // Always close left sidebar on campaign pages
			userOpenedSidebarRef.current = false; // Reset manual open flag
		}
		// Leaving campaign page - reopen left sidebar on desktop and reset manual open flag
		else if (!isCampaignPath && wasCampaignPath) {
			userOpenedSidebarRef.current = false; // Reset flag when leaving campaign pages
			if (window.innerWidth >= 768) {
				openLeftSidebar();
			}
		}
	}, [pathname, isCampaignPath, handleCloseSidebar, closeLeftSidebar, openLeftSidebar]);

	// Close left sidebar when entering editor mode
	useEffect(() => {
		if (isEditorMode) {
			closeLeftSidebar();
		}
	}, [isEditorMode, closeLeftSidebar]);

	// On desktop, sidebar is always visible (open by default), on mobile it's hidden (hamburger menu)
	const sidebarWidthPx =
		isLeftSidebarOpen && width >= BREAKPOINTS.mobile
			? parseInt(LEFT_SIDEBAR_WIDTH)
			: 0;
	const leftOffset =
		isLeftSidebarOpen && width >= BREAKPOINTS.mobile
			? `${sidebarWidthPx}px`
			: '0px';

	// Detect actual mobile device - use screen.width instead of window.innerWidth
	// to avoid issues with viewport manipulation
	const isMobileDevice = typeof window !== 'undefined' && window.screen.width < BREAKPOINTS.mobile;

	// On mobile, when right sidebar is open, it takes full width and content should be hidden
	const shouldHideContent = isMobileDevice && isSidebarOpen;

	const rightOffset = isSidebarOpen
		? isMobileDevice ? '0px' : RIGHT_SIDEBAR_WIDTH  // On mobile, no offset needed since sidebar is full screen
		: RIGHT_SIDEBAR_WIDTH_COLLAPSED;

	const containerWidth = shouldHideContent
		? '0px'  // Hide content on mobile when sidebar is open
		: `calc(100vw - ${leftOffset} - ${rightOffset})`;

	const sidebarContextValue = {
		isSidebarOpen,
		activePanel,
		handleOpenSidebar,
		handlePanelToggle,
		handleCloseSidebar,
	};


	return (
		<RightSidebarProvider value={sidebarContextValue}>
			{user && (
				<RightSidebar
					isOpen={isSidebarOpen}
					activePanel={activePanel}
					onPanelToggle={handlePanelToggle}
					onClose={handleCloseSidebar}
					isScreenBlocked={isScreenBlocked}
					onOpenSidebar={handleOpenSidebar}
				/>
			)}
			<Box h="100vh" w="100vw" m={0}>
				<>
					{/* Sidebar is always rendered so it can open on all pages */}
					{!shouldHideContent && <Sidebar />}

					<TopBar />
					<Box
						h={isCampaignPath ? { base: `calc(100vh - ${TOP_BAR_HEIGHT})`, md: `calc(100vh - ${TOP_BAR_HEIGHT} - 60px)` } : `calc(100vh - ${TOP_BAR_HEIGHT})`}
						w={containerWidth}
						mt={isCampaignPath ? { base: TOP_BAR_HEIGHT, md: `calc(${TOP_BAR_HEIGHT} + 60px)` } : TOP_BAR_HEIGHT}
						mr={rightOffset}
						ml={leftOffset}
						p={{ base: '8px', md: '20px', lg: '28px' }}
						bg="white"
						display={shouldHideContent ? 'none' : 'flex'}
						flexDirection="column"
						overflowX="unset"
						overflowY="unset"
						zIndex={1}
					>
						{isChatOpen && <AssistantChat />}
						<Outlet />
					</Box>
				</>

				{isAdTemplateModalOpen && (
					<SelectTemplateModal
						isOpen={isAdTemplateModalOpen}
						onClose={() => setAdTemplateModalOpen(false)}
						source="sidebar"
					/>
				)}

				<TrialEndingModal
					isOpen={isTrialModalOpen}
					onClose={onTrialModalClose}
				/>

				{/* Onboarding Components */}
				<WelcomeWizard />
				<MenuTour />
				<GuidedCursor
					isActive={showGuidedCursor}
					steps={[
						{
							targetId: 'menu-campaigns',
							message: 'Click on "Ad Campaigns"',
							subMessage: 'This opens the campaigns menu',
						},
						{
							targetId: 'submenu-create-ads',
							message: 'Now click "Create Ads"',
							subMessage: 'Start creating your first campaign!',
						},
					]}
					onComplete={() => {
						console.log('🎯 [GuidedCursor] onComplete called - completing Wizard 1');
						setWizard1Completed();
						closeGuidedCursor();
					}}
					onSkip={() => {
						console.log('🎯 [GuidedCursor] onSkip called - skipping Wizard 1');
						setWizard1Completed();
						closeGuidedCursor();
					}}
				/>

			</Box>
		</RightSidebarProvider>
	);
};

const MainLayout = () => {
	return (
		<LeftSidebarProvider>
			<MainLayoutContent />
		</LeftSidebarProvider>
	);
};

export default MainLayout;
